import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { showLabel } from '@/utils/helper';
import { OnoffType } from '@/api/enum/common.enum';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { DefaultValues, useForm } from 'react-hook-form';
import {
	useBatchUpdateMeditationHasNewMutation,
	useGetMeditationPublishingListQuery,
} from '@/api/exercise/meditationApiSlice';
import { DataGrid } from '../MeditationListSummaryPage';
import { MeditationPageDTO } from '@/api/DTO/exercise/meditation.interface';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';

interface Props {}

export default function SetMeditationNewDialog(props: Props) {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [batchUpdateMeditationHasNew] =
		useBatchUpdateMeditationHasNewMutation();
	const showModal = useModal();

	const onoffList = [
		{
			id: OnoffType.off,
			name: t('information.off'),
		},
		{
			id: OnoffType.on,
			name: t('information.on'),
		},
	];
	const yesOrNoList = [
		{ id: true, name: t('meditation.yes') },
		{ id: false, name: t('meditation.no') },
	];
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetMeditationPublishingListQuery(null, {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	const defaultValues: DefaultValues<any> = {};

	const methods = useForm<any>({
		defaultValues,
	});

	const onSubmit = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('meditation.setNewExercise')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('meditation.setNewExerciseTips')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const data: any[] = Object.entries(methods.getValues()).map(
						([key, value]) => ({
							seriesId: key,
							hasNew: value,
						})
					);
					const response = await batchUpdateMeditationHasNew({ list: data });
					if ('data' in response) {
						enqueueSnackbar(t('meditation.setNewExerciseSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						handleClose();
					}
				}}
			/>
		));
	};

	const handleClose = () => {
		setOpen(false);
	};

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('meditation.seriesTitleZh'),
			flex: 25,
		},
		{
			field: 'singleCount',
			headerName: t('meditation.exerciseCount'),
			flex: 25,
		},
		{
			field: 'singleHasNewCount',
			headerName: t('meditation.newExerciseCount'),
			flex: 25,
		},
		{
			field: 'status',
			headerName: t('meditation.seriesStatus'),
			flex: 10,
			renderCell: (params: any) => (
				<Chip
					color={
						params.row.published == OnoffType.on ? 'chipPrimary' : 'default'
					}
					label={showLabel(params.row.published, onoffList)}
				/>
			),
		},
		{
			field: 'isNewExercise',
			headerName: t('meditation.setNewExerciseItem'),
			flex: 25,
			renderCell: (params: any) => (
				<>
					<AppRadioGroup
						control={methods.control}
						name={params.row.seriesId}
						row
						options={yesOrNoList}
					/>
				</>
			),
		},
	];

	const transformResponse = useCallback((response?: MeditationPageDTO[]) => {
		if (!response) return;
		const newResponse: DataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				id: item.seriesId,
				isNewExercise:
					item.singleCount == item.singleHasNewCount ? true : false,
			});

			methods.setValue(
				item.seriesId,
				item.singleCount == item.singleHasNewCount ? true : false
			);
		});

		return newResponse;
	}, []);

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<LibraryBooksOutlinedIcon />}
				onClick={() => setOpen(true)}
			>
				{t('meditation.setNewExercise')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('meditation.setNewExercise')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Grid container alignItems='stretch' columnSpacing={3} marginTop={3}>
						<Grid item xs display='flex'>
							<LocalDataGrid
								rows={transformResponse(propertySummary?.data) ?? []}
								columns={columns}
								rowCount={0}
								getRowId={(row) => row.seriesId}
								pagination={false}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
